import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { queryKeys, sourceDeviceBlocksApi } from "../../lib/api/resources";
import type { SourceDeviceBlockGroup } from "../../lib/api/types";
import { useToast } from "../../app/toast";
import {
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Skeleton,
  Table,
  Td,
  Th,
} from "../../components/ui";

/**
 * Sources offered as autocomplete, matching the chain editor's list.
 *
 * Convenience only. The server accepts any name, which is what lets a source be silenced before —
 * or after — any app implements it.
 */
const KNOWN_SOURCES = [
  "dramyeri",
  "admob",
  "applovin",
  "unity",
  "pangle",
  "mintegral",
];

/**
 * Installations on which a given ad source is silenced (ADS-0052).
 *
 * <p>The operator and their partner run the app on their own phones daily. AdMob treats the taps
 * that produces on our own inventory as invalid traffic and penalises the account for it. This
 * list turns that off for those handsets.
 *
 * <p>Two things about the shape of it are deliberate:
 *
 * <ul>
 *   <li><strong>The block is per source, not per device.</strong> A silenced phone keeps receiving
 *       <code>dramyeri</code> campaigns — our own inventory, with nobody to defraud — which is what
 *       the operator uses those phones to check.
 *   <li><strong>It is global, not per placement.</strong> A source that must go quiet on a device
 *       goes quiet everywhere on it. Four copies of the same decision would only ever drift.
 * </ul>
 *
 * <p>Unlike a chain edit, this takes effect on the device's next ad load rather than when its cache
 * expires: a filtered document is served uncacheable.
 */
export function SourceDeviceBlocksCard() {
  const toast = useToast();
  const queryClient = useQueryClient();

  const blocks = useQuery({
    queryKey: queryKeys.sourceDeviceBlocks,
    queryFn: sourceDeviceBlocksApi.list,
  });

  const invalidate = () =>
    void queryClient.invalidateQueries({
      queryKey: queryKeys.sourceDeviceBlocks,
    });

  const create = useMutation({
    mutationFn: sourceDeviceBlocksApi.create,
    onSuccess: () => {
      toast.success("Fuente desactivada en esa instalación");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: sourceDeviceBlocksApi.remove,
    onSuccess: () => {
      toast.success("Fuente reactivada en esa instalación");
      invalidate();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  return (
    <Card className="flex flex-col gap-4">
      <header>
        <h2 className="text-title-md text-text-primary">
          Dispositivos excluidos
        </h2>
        <p className="text-body-md text-text-secondary">
          Instalaciones que no deben ver anuncios de una fuente. Se aplica a
          todos los espacios y surte efecto en la siguiente carga de anuncio, al
          agregar y al quitar. Las demás fuentes siguen sirviendo con
          normalidad.
        </p>
      </header>

      <NewBlockForm
        onCreate={(body) => create.mutate(body)}
        pending={create.isPending}
      />

      {blocks.isLoading ? (
        <Skeleton className="h-24 w-full" />
      ) : blocks.isError ? (
        <ErrorState
          message={(blocks.error as Error).message}
          onRetry={() => void blocks.refetch()}
        />
      ) : blocks.data!.length === 0 ? (
        <EmptyState
          title="Ninguna instalación excluida"
          description="El id de instalación aparece al pie de la pantalla Utilerías de la app; al tocarlo se copia."
        />
      ) : (
        blocks.data!.map((group) => (
          <SourceGroupTable
            key={group.source}
            group={group}
            onRemove={(id) => remove.mutate(id)}
            pending={remove.isPending}
          />
        ))
      )}
    </Card>
  );
}

/**
 * Adds one installation to one source's list.
 *
 * The button says why it is disabled rather than silently doing nothing, which is the confusion
 * ADS-0041 recorded on the chain form.
 */
function NewBlockForm({
  onCreate,
  pending,
}: {
  onCreate: (body: {
    source: string;
    deviceId: string;
    label?: string;
  }) => void;
  pending: boolean;
}) {
  const [source, setSource] = useState("admob");
  const [deviceId, setDeviceId] = useState("");
  const [label, setLabel] = useState("");

  const trimmedSource = source.trim();
  const trimmedDevice = deviceId.trim();
  const incomplete = trimmedSource.length === 0 || trimmedDevice.length === 0;

  const submit = () => {
    if (incomplete) {
      return;
    }
    onCreate({
      source: trimmedSource,
      deviceId: trimmedDevice,
      label: label.trim() || undefined,
    });
    setDeviceId("");
    setLabel("");
  };

  return (
    <div className="flex flex-wrap items-end gap-3">
      <Field label="Fuente" htmlFor="block-source">
        <Input
          id="block-source"
          list="block-source-options"
          value={source}
          onChange={(event) => setSource(event.target.value)}
          className="w-40"
        />
        <datalist id="block-source-options">
          {KNOWN_SOURCES.map((known) => (
            <option key={known} value={known} />
          ))}
        </datalist>
      </Field>

      <Field
        label="Id de instalación"
        htmlFor="block-device"
        hint="Se copia desde la pantalla Utilerías de la app."
      >
        <Input
          id="block-device"
          value={deviceId}
          onChange={(event) => setDeviceId(event.target.value)}
          className="w-96 font-mono"
        />
      </Field>

      <Field label="Nombre" htmlFor="block-label">
        <Input
          id="block-label"
          placeholder="Celular de Ramsés"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          className="w-56"
        />
      </Field>

      <Button
        onClick={submit}
        loading={pending}
        disabled={incomplete}
        title={
          incomplete
            ? "Indica la fuente y el id de instalación"
            : "Desactivar esta fuente en esta instalación"
        }
      >
        Desactivar
      </Button>
    </div>
  );
}

/** One source and the installations it is silenced on. */
function SourceGroupTable({
  group,
  onRemove,
  pending,
}: {
  group: SourceDeviceBlockGroup;
  onRemove: (id: string) => void;
  pending: boolean;
}) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-label-md font-mono uppercase text-text-secondary">
        {group.source}
      </h3>
      <Table>
        <thead>
          <tr>
            <Th>Nombre</Th>
            <Th>Id de instalación</Th>
            <Th>&nbsp;</Th>
          </tr>
        </thead>
        <tbody>
          {group.devices.map((device) => (
            <tr key={device.id}>
              <Td>{device.label ?? "Sin nombre"}</Td>
              <Td className="font-mono">{device.deviceId}</Td>
              <Td className="text-right">
                <Button
                  variant="ghost"
                  onClick={() => onRemove(device.id)}
                  disabled={pending}
                >
                  Reactivar
                </Button>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>
    </section>
  );
}
